<template>
  <div>
    <!-- Header -->
    <div class="flex items-start justify-between mb-4">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-semibold text-gray-900 font-mono truncate">
            {{ schema.snowflake_schema }}.{{ schema.table_name }}
          </h3>
          <span class="text-xs px-1.5 py-0.5 rounded" :class="sheetTypeClass(schema.sheet_type)">
            {{ sheetTypeLabel(schema.sheet_type) }}
          </span>
          <span v-if="schema.dont_parse" class="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
            parsing off
          </span>
        </div>
        <p class="text-xs text-gray-400 mt-0.5">
          {{ (schema.columns || []).length }} columns
          · created {{ formatDate(schema.created_at) }}
          <template v-if="schema.source_file"> · from {{ schema.source_file }}</template>
        </p>
      </div>
      <div v-if="canManage" class="flex items-center gap-2 shrink-0 ml-4">
        <template v-if="!editing">
          <button class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:border-gray-400" @click="beginEdit">
            Edit
          </button>
          <button
            class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:border-gray-400 disabled:opacity-50"
            :disabled="creatingTable"
            @click="createTable"
          >
            {{ creatingTable ? 'Creating…' : 'Create table' }}
          </button>
          <button class="text-xs px-2.5 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50" @click="deleteModalOpen = true">
            Delete
          </button>
        </template>
        <template v-else>
          <button class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:border-gray-400" @click="cancelEdit">
            Cancel
          </button>
          <button
            class="text-xs px-2.5 py-1 rounded bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </template>
      </div>
    </div>

    <!-- Used by routines -->
    <div class="mb-4">
      <span class="text-xs font-medium text-gray-500 mr-2">Used by routines:</span>
      <template v-if="usingRoutines.length">
        <button
          v-for="routine in usingRoutines"
          :key="routine.id"
          class="inline-flex items-center gap-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded px-2 py-0.5 mr-1.5"
          @click="$emit('select-routine', routine.id)"
        >
          <span class="h-1.5 w-1.5 rounded-full" :class="statusDotClass(routine.last_run_status)"></span>
          {{ routine.name }}
        </button>
      </template>
      <span v-else class="text-xs text-amber-600">
        none — no routine parses into this schema
      </span>
    </div>

    <SchemaForm v-model="draft" :editable="editing" class="mb-6" />

    <!-- Scoped history -->
    <div>
      <p class="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Parse history</p>
      <ParseHistoryTable :key="'schema-history-' + schema.id" :schema-id="schema.id" :can-manage="canManage" />
    </div>

    <!-- Delete confirmation -->
    <div
      v-if="deleteModalOpen"
      class="fixed inset-0 z-40 bg-black/30 flex items-center justify-center"
      @click.self="deleteModalOpen = false"
    >
      <div class="bg-white rounded-lg border border-gray-200 w-[28rem] p-4">
        <h4 class="text-sm font-semibold text-gray-900 mb-2">Delete schema</h4>
        <p class="text-xs text-gray-600 mb-3">
          <strong>Soft delete</strong> hides the schema from parsing but keeps its history.
          <strong>Hard delete</strong> permanently removes the schema and its parse history —
          the Snowflake table itself is not dropped.
        </p>
        <label class="block text-xs text-gray-500 mb-1">
          Type <span class="font-mono text-gray-800">{{ schema.table_name }}</span> to enable hard delete
        </label>
        <input
          v-model="deleteConfirmText"
          type="text"
          class="w-full border border-gray-200 rounded px-2 py-1.5 text-sm font-mono mb-3 focus:outline-none focus:border-gray-400"
        />
        <div class="flex items-center justify-end gap-2">
          <button class="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600" @click="deleteModalOpen = false">
            Cancel
          </button>
          <button
            class="text-xs px-2.5 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
            :disabled="deleting"
            @click="doDelete(false)"
          >
            Soft delete
          </button>
          <button
            class="text-xs px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
            :disabled="deleting || deleteConfirmText !== schema.table_name"
            @click="doDelete(true)"
          >
            Hard delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {
  formatDate, sheetTypeClass, sheetTypeLabel, statusDotClass,
} from '@/utils/etlFormat';
import SchemaForm from './SchemaForm.vue';
import ParseHistoryTable from './ParseHistoryTable.vue';

export default {
  name: 'SchemaDetail',
  components: { SchemaForm, ParseHistoryTable },
  props: {
    schema: { type: Object, required: true },
    routines: { type: Array, default: () => [] },
    canManage: { type: Boolean, default: false },
  },
  emits: ['changed', 'deleted', 'toast', 'select-routine'],
  data() {
    return {
      draft: this.cloneSchema(this.schema),
      editing: false,
      saving: false,
      creatingTable: false,
      deleteModalOpen: false,
      deleteConfirmText: '',
      deleting: false,
    };
  },
  computed: {
    usingRoutines() {
      return this.routines.filter((r) => (r.schemas || []).includes(this.schema.id));
    },
  },
  watch: {
    schema(next) {
      this.draft = this.cloneSchema(next);
      this.editing = false;
      this.deleteModalOpen = false;
      this.deleteConfirmText = '';
    },
  },
  methods: {
    formatDate,
    sheetTypeClass,
    sheetTypeLabel,
    statusDotClass,
    cloneSchema(schema) {
      return JSON.parse(JSON.stringify(schema));
    },
    beginEdit() {
      this.draft = this.cloneSchema(this.schema);
      this.editing = true;
    },
    cancelEdit() {
      this.draft = this.cloneSchema(this.schema);
      this.editing = false;
    },
    async save() {
      this.saving = true;
      try {
        const { data } = await axios.put(`/api/etl_module/schemas/${this.schema.id}/`, this.draft);
        this.editing = false;
        this.$emit('changed', data);
        this.$emit('toast', { message: `Schema ${data.table_name} saved`, kind: 'success' });
      } catch (err) {
        const detail = err?.response?.data;
        const message = typeof detail === 'object' && detail !== null
          ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join('; ')
          : 'Failed to save schema';
        this.$emit('toast', { message, kind: 'error' });
      }
      this.saving = false;
    },
    async createTable() {
      this.creatingTable = true;
      try {
        const { data } = await axios.post(`/api/etl_module/schemas/${this.schema.id}/create-table/`);
        this.$emit('toast', {
          message: data.message || `Table ${this.schema.table_name} created`,
          kind: 'success',
        });
      } catch (err) {
        this.$emit('toast', {
          message: err?.response?.data?.error || 'Failed to create Snowflake table',
          kind: 'error',
        });
      }
      this.creatingTable = false;
    },
    async doDelete(hard) {
      this.deleting = true;
      try {
        const url = hard
          ? `/api/etl_module/schemas/${this.schema.id}/hard-delete/`
          : `/api/etl_module/schemas/${this.schema.id}/`;
        await axios.delete(url);
        this.deleteModalOpen = false;
        this.$emit('deleted', this.schema.id);
        this.$emit('toast', {
          message: `Schema ${this.schema.table_name} ${hard ? 'permanently deleted' : 'deleted'}`,
          kind: 'success',
        });
      } catch (err) {
        this.$emit('toast', {
          message: err?.response?.data?.error || 'Failed to delete schema',
          kind: 'error',
        });
      }
      this.deleting = false;
    },
  },
};
</script>
